import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDraftAssistant } from '../hooks/useDraftAssistant';
import { useLeagueSettings } from '../context/LeagueSettingsContext';
import type { LeagueSettings, DraftRecommendation } from '../types/draft';
import type { Player } from '../types/player';
import { PageLoader } from '../components/LoadingSpinner';
import { getPlayerImageWithOverrides, debugPlayerImageMapping } from '../utils/playerImageFix';

const DraftAssistant: React.FC = () => {
  const { leagueData, hasImportedSettings } = useLeagueSettings();
  
  const [leagueSettings, setLeagueSettings] = useState<LeagueSettings>(() => {
    // Convert imported Sleeper settings to Draft Assistant format if available
    if (hasImportedSettings) {
      const imported = leagueData.settings;
      return {
        teams: imported.teamCount,
        scoringFormat: imported.scoring.pointsPerReception > 0 ? 
          (imported.scoring.pointsPerReception === 1 ? 'ppr' : 'half-ppr') : 'standard',
        rosterPositions: {
          qb: imported.roster.QB,
          rb: imported.roster.RB,
          wr: imported.roster.WR,
          te: imported.roster.TE,
          k: imported.roster.K || 1,
          def: imported.roster.DEF || 1,
          flex: imported.roster.FLEX
        },
        flexPositions: {
          rbWrTe: imported.roster.FLEX,
          rbWr: 0,
          wrTe: 0,
          qbRbWrTe: imported.roster.SUPERFLEX || 0
        },
        benchSize: 6
      };
    }
    
    // Default settings if no import
    return {
      teams: 12,
      scoringFormat: 'ppr',
      rosterPositions: {
        qb: 1,
        rb: 2,
        wr: 2,
        te: 1,
        k: 1,
        def: 1,
        flex: 1
      },
      flexPositions: {
        rbWrTe: 1,
        rbWr: 0,
        wrTe: 0,
        qbRbWrTe: 0
      },
      benchSize: 6
    };
  });

  const [draftPosition, setDraftPosition] = useState(6);
  const [showSettings, setShowSettings] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPlayerSearch, setShowPlayerSearch] = useState(false);
  const [isRunningMockDraft, setIsRunningMockDraft] = useState(false);
  const [showMockResults, setShowMockResults] = useState(false);
  const [draftType, setDraftType] = useState<'snake' | 'traditional'>('snake');
  const [draftPositionMode, setDraftPositionMode] = useState<'manual' | 'random'>('manual');
  const [draftStatus, setDraftStatus] = useState<'inactive' | 'starting' | 'active' | 'ending'>('inactive');
  const [currentPickTimeout, setCurrentPickTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Track CPU team rosters for intelligent picking
  const [cpuTeamRosters, setCpuTeamRosters] = useState<Record<number, Record<string, number>>>({});
  
  // Track recent picks for display
  const [recentPicks, setRecentPicks] = useState<Array<{
    pick: number;
    teamId: number;
    playerName: string;
    position: string;
    team: string;
    timestamp: Date;
  }>>([]);
  
  // Track current picking team status
  const [currentPickingStatus, setCurrentPickingStatus] = useState<string>('');
  
  // Track draft completion
  const [isDraftCompleted, setIsDraftCompleted] = useState<boolean>(false);
  
  // Ref to prevent stale closures
  const draftStatusRef = useRef(draftStatus);
  useEffect(() => {
    draftStatusRef.current = draftStatus;
  }, [draftStatus]);

  const {
    draftState,
    recommendations,
    draftStrategy,
    error,
    loading,
    updateLeagueSettings,
    makePick,
    resetDraft,
    mockDraftResult
  } = useDraftAssistant(leagueSettings, { userTeamId: draftPosition });

  // Update league settings when they change
  useEffect(() => {
    updateLeagueSettings(leagueSettings);
  }, [leagueSettings, updateLeagueSettings]);

  // Reset draft when draft position changes (but not during active draft)
  useEffect(() => {
    if (!loading && draftStatus === 'inactive' && draftState.userTeamId !== draftPosition) {
      console.log('Draft position changed, resetting draft state');
      resetDraft(draftPosition);
    }
  }, [draftPosition, resetDraft, loading, draftState.userTeamId, draftStatus]);

  // Filter available players for search
  const filteredPlayers = useMemo(() => {
    if (!searchQuery) {
      // Show highest projected players that aren't in top 8 recommendations
      const recommendedPlayerIds = new Set(recommendations.slice(0, 8).map(rec => rec.player.id));
      return draftState.availablePlayers
        .filter(player => player && player.id && !recommendedPlayerIds.has(player.id))
        .sort((a, b) => (b.projection || 0) - (a.projection || 0))
        .slice(0, 50); // Show top 50 by projection
    }
    
    const queryLower = searchQuery.toLowerCase();
    return draftState.availablePlayers
      .filter(player => {
        // Ensure player object and required properties exist
        if (!player || !player.id) return false;
        
        const name = (player.name || '').toLowerCase();
        const position = (player.position || '').toLowerCase();
        const team = (player.team || '').toLowerCase();
        
        return name.includes(queryLower) ||
               position.includes(queryLower) ||
               team.includes(queryLower);
      })
      .slice(0, 20); // Limit to 20 results
  }, [searchQuery, draftState.availablePlayers, recommendations]);

  // Player image mapping using ESPN ID approach (like Trade Calculator)
  const [nameToEspnId, setNameToEspnId] = useState<Record<string, string>>({});

  // Fetch ESPN images for players using the same approach as Trade Calculator
  useEffect(() => {
    const fetchPlayerImages = async () => {
      try {
        const espnRes = await fetch('https://sports.core.api.espn.com/v3/sports/football/nfl/athletes?limit=18000');
        if (!espnRes.ok) throw new Error('Failed to fetch ESPN data');
        const espnData = await espnRes.json();
        const espnAthletes = espnData.items || [];
        
        const nameToIdMap: Record<string, string> = {};
        espnAthletes.forEach((athlete: any) => {
          if (athlete.fullName && athlete.id) {
            nameToIdMap[athlete.fullName.toLowerCase()] = athlete.id.toString();
          }
        });
        
        console.log(`Loaded ${Object.keys(nameToIdMap).length} ESPN player ID mappings`);
        setNameToEspnId(nameToIdMap);
      } catch (error) {
        console.error('Error fetching ESPN player IDs:', error);
      }
    };

    fetchPlayerImages();
  }, []);

  const getPlayerImage = (playerName: string | undefined) => {
    if (!playerName) {
      return 'https://a.espncdn.com/i/headshots/nophoto.png';
    }
    const espnId = nameToEspnId[playerName.toLowerCase()];
    
    // Debug image mapping for specific players
    if (['Lamar Jackson', 'Josh Allen', 'Patrick Mahomes'].includes(playerName)) {
      debugPlayerImageMapping(playerName, espnId);
    }
    
    return getPlayerImageWithOverrides(
      playerName,
      espnId,
      'https://a.espncdn.com/i/headshots/nophoto.png'
    );
  };

  const handleLeagueSettingsChange = (key: keyof LeagueSettings, value: any) => {
    setLeagueSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRosterPositionChange = (position: string, value: number) => {
    setLeagueSettings(prev => ({
      ...prev,
      rosterPositions: {
        ...prev.rosterPositions,
        [position]: value
      }
    }));
  };

  const handleFlexPositionChange = (flexType: string, value: number) => {
    setLeagueSettings(prev => ({
      ...prev,
      flexPositions: {
        ...prev.flexPositions,
        [flexType]: value
      }
    }));
  };

  const handleStartDraft = async () => {
    setIsRunningMockDraft(true);
    try {
      console.log('🚀 Starting draft initialization...');
      
      // Set draft status to starting to prevent useEffect interference
      setDraftStatus('starting');
      setShowPlayerSearch(false);
      
      // Assign random position if selected
      let finalDraftPosition = draftPosition;
      if (draftPositionMode === 'random') {
        finalDraftPosition = Math.floor(Math.random() * leagueSettings.teams) + 1;
        setDraftPosition(finalDraftPosition);
      }

      console.log(`Starting draft at position ${finalDraftPosition} with ${draftType} format`);
      
      // Reset the draft state
      await resetDraft(finalDraftPosition);
      
      // Reset CPU team rosters and clear draft history
      setCpuTeamRosters({});
      setRecentPicks([]);
      setCurrentPickingStatus('');
      setIsDraftCompleted(false);
      
      // Set draft as fully active
      setDraftStatus('active');
      
      console.log('🚀 Draft started! Draft is now active, processing first pick...');
      
      // Start the draft with a direct approach to prevent state interference
      setTimeout(() => {
        console.log('⏰ Processing first pick directly...');
        // Force the ref to be updated in case of state race conditions
        draftStatusRef.current = 'active';
        processNextPick();
      }, 0);
      
    } catch (error) {
      console.error('Error starting draft:', error);
      setDraftStatus('inactive'); // Ensure draft is not active on error
    } finally {
      setIsRunningMockDraft(false);
    }
  };

  const handleEndDraft = () => {
    setDraftStatus('inactive');
    setShowPlayerSearch(false);
    setRecentPicks([]); // Clear pick history
    setCurrentPickingStatus(''); // Clear picking status
    setCpuTeamRosters({}); // Reset CPU rosters
    setIsDraftCompleted(false); // Reset completion status
    if (currentPickTimeout) {
      clearTimeout(currentPickTimeout);
      setCurrentPickTimeout(null);
    }
    console.log('Draft ended by user');
  };

  // Calculate which team should be picking based on draft type
  const getCurrentPickingTeam = (): number => {
    const round = draftState.round;
    const pickInRound = ((draftState.currentPick - 1) % leagueSettings.teams) + 1;
    
    if (draftType === 'snake' && round % 2 === 0) {
      // Even rounds go in reverse order for snake draft
      return leagueSettings.teams - pickInRound + 1;
    } else {
      // Odd rounds (or traditional draft) go in normal order
      return pickInRound;
    }
  };

  // Check if it's the user's turn
  const isUserTurn = draftStatus === 'active' && getCurrentPickingTeam() === draftState.userTeamId;

  // Calculate position caps based on league settings
  const getPositionCaps = () => {
    const { rosterPositions, flexPositions } = leagueSettings;
    
    // Total flex spots that can use RB/WR/TE (with safe defaults)
    const flexSpots = (flexPositions.rbWrTe || 0) + (flexPositions.rbWr || 0) + (flexPositions.wrTe || 0);
    
    // Calculate reasonable caps based on starting positions + flex eligibility + reasonable depth
    return {
      QB: Math.max(2, rosterPositions.qb + (flexPositions.qbRbWrTe || 0) + 1), // QBs + superflex + backup
      RB: Math.max(4, rosterPositions.rb + flexSpots + 2), // RBs + FLEX eligibility + depth
      WR: Math.max(4, rosterPositions.wr + flexSpots + 2), // WRs + FLEX eligibility + depth
      TE: Math.max(2, rosterPositions.te + (flexPositions.rbWrTe || 0) + (flexPositions.wrTe || 0) + 1), // TEs + TE-eligible FLEX + backup
      K: Math.max(1, rosterPositions.k || 0), // Usually just 1
      DEF: Math.max(1, rosterPositions.def || 0) // Usually just 1
    };
  };

  // Check if user can draft a specific position
  const canDraftPosition = (position: string): boolean => {
    const positionCaps = getPositionCaps();
    const currentCount = draftState.userRoster.filter(p => p.position === position).length;
    const cap = positionCaps[position as keyof typeof positionCaps] || 6; // Default to reasonable bench limit
    return currentCount < cap;
  };

  // Initialize CPU team rosters when draft starts
  useEffect(() => {
    if (draftStatus === 'active' && Object.keys(cpuTeamRosters).length === 0) {
      const initialRosters: Record<number, Record<string, number>> = {};
      for (let i = 1; i <= leagueSettings.teams; i++) {
        initialRosters[i] = {
          QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0, BENCH: 0
        };
      }
      setCpuTeamRosters(initialRosters);
      console.log('Initialized CPU team rosters:', initialRosters);
    }
  }, [draftStatus, leagueSettings.teams, cpuTeamRosters]);

  // Update team rosters when picks are made
  useEffect(() => {
    if (draftState.draftedPlayers.length > 0 && Object.keys(cpuTeamRosters).length > 0) {
      const updatedRosters: Record<number, Record<string, number>> = {};
      
      // Initialize all teams
      for (let i = 1; i <= leagueSettings.teams; i++) {
        updatedRosters[i] = {
          QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0, BENCH: 0
        };
      }
      
      // Count all drafted players
      draftState.draftedPlayers.forEach(pick => {
        const player = draftState.availablePlayers.find(p => String(p.id) === pick.playerId) || 
                      draftState.userRoster.find(p => String(p.id) === pick.playerId);
        if (player && updatedRosters[pick.teamId]) {
          const position = player.position.toUpperCase();
          if (updatedRosters[pick.teamId][position] !== undefined) {
            updatedRosters[pick.teamId][position]++;
          } else {
            updatedRosters[pick.teamId]['BENCH']++;
          }
        }
      });
      
      setCpuTeamRosters(updatedRosters);
      console.log('Updated CPU rosters:', updatedRosters);
    }
  }, [draftState.draftedPlayers, leagueSettings.teams]);

  // Auto-process next pick when draft state updates (during active draft)
  useEffect(() => {
    if (draftStatus === 'active' && draftState.currentPick > 1) {
      console.log(`📊 Draft state updated - currentPick: ${draftState.currentPick}`);
      
      // Clear any existing timeout
      if (currentPickTimeout) {
        clearTimeout(currentPickTimeout);
        setCurrentPickTimeout(null);
      }
      
      // Don't auto-process if it's currently the user's turn - let them make their pick
      const currentTeam = getCurrentPickingTeam();
      if (currentTeam === draftState.userTeamId) {
        console.log("🙋‍♂️ It's user's turn - waiting for manual pick");
        return;
      }
      
      // Process next pick after a short delay to ensure all state updates are complete
      const timeout = setTimeout(() => {
        // Double-check it's still not the user's turn before processing
        const stillCurrentTeam = getCurrentPickingTeam();
        if (stillCurrentTeam !== draftState.userTeamId) {
          processNextPick();
        }
      }, 500);
      setCurrentPickTimeout(timeout);
    }
  }, [draftState.currentPick, draftStatus]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (currentPickTimeout) {
        clearTimeout(currentPickTimeout);
      }
    };
  }, [currentPickTimeout]);

  // Debug useEffect to catch what's changing draftStatus
  useEffect(() => {
    console.log(`🔄 draftStatus changed to: ${draftStatus}`);
    if (draftStatus === 'inactive') {
      console.trace('📍 Stack trace of what set draftStatus to inactive:');
    }
  }, [draftStatus]); // Removed cpuTeamRosters dependency

  // Process the next pick (AI or waiting for user)
  const processNextPick = () => {
    console.log('🔍 processNextPick called - draftStatus:', draftStatus, '- draftStatusRef.current:', draftStatusRef.current);
    if (draftStatusRef.current !== 'active') {
      console.log('❌ Draft not active, stopping pick processing');
      return;
    }
    
    // Check if draft is complete
    if (draftState.currentPick > leagueSettings.teams * 15) {
      console.log('🏁 Draft completed!');
      setDraftStatus('inactive');
      setIsDraftCompleted(true);
      return;
    }
    
    const currentTeam = getCurrentPickingTeam();
    console.log(`🎯 Processing pick ${draftState.currentPick} - Team ${currentTeam}'s turn`);
    
    if (currentTeam === draftState.userTeamId) {
      // It's the user's turn - they need to make a pick
      console.log("🙋‍♂️ It's your turn to pick! Waiting for user action...");
      setCurrentPickingStatus('');
      return;
    }
    
    // Validate we have available players
    if (draftState.availablePlayers.length === 0) {
      console.log('⚠️ No available players for AI pick');
      return;
    }
    
    // AI makes a pick almost instantly
    console.log(`🤖 AI team ${currentTeam} making pick...`);
    setCurrentPickingStatus(`Team ${currentTeam} is selecting a player...`);
    
    setTimeout(() => {
      if (draftStatusRef.current === 'active' && getCurrentPickingTeam() === currentTeam) {
        // Double-check conditions haven't changed
        makeAIPick(currentTeam);
        setCurrentPickingStatus('');
      } else {
        console.log('⚠️ Draft conditions changed, skipping AI pick');
        setCurrentPickingStatus('');
      }
    }, 200); // Almost instant (0.2 seconds)
  };

  // Get team needs based on current roster
  const getTeamNeeds = (teamId: number, round: number): string[] => {
    const roster = cpuTeamRosters[teamId] || { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0, BENCH: 0 };
    const needs: string[] = [];
    
    console.log(`Team ${teamId} current roster:`, roster);
    
    // Early rounds (1-4): Focus on starters
    if (round <= 4) {
      if ((roster.RB || 0) < leagueSettings.rosterPositions.rb) needs.push('RB');
      if ((roster.WR || 0) < leagueSettings.rosterPositions.wr) needs.push('WR');
      if ((roster.TE || 0) < leagueSettings.rosterPositions.te) needs.push('TE');
      if ((roster.QB || 0) < leagueSettings.rosterPositions.qb && round >= 3) needs.push('QB');
    }
    // Mid rounds (5-10): Fill remaining starters + depth
    else if (round <= 10) {
      if ((roster.WR || 0) < leagueSettings.rosterPositions.wr + 1) needs.push('WR');
      if ((roster.RB || 0) < leagueSettings.rosterPositions.rb + 1) needs.push('RB');
      if ((roster.QB || 0) < leagueSettings.rosterPositions.qb) needs.push('QB');
      if ((roster.TE || 0) < leagueSettings.rosterPositions.te + 1) needs.push('TE');
    }
    // Late rounds (11+): Best available + K/DEF
    else {
      if ((roster.K || 0) === 0) needs.push('K');
      if ((roster.DEF || 0) === 0) needs.push('DEF');
      needs.push('RB', 'WR', 'TE', 'QB'); // Depth
    }
    
    const finalNeeds = needs.length > 0 ? needs : ['RB', 'WR', 'TE', 'QB']; // Fallback to skill positions
    console.log(`Team ${teamId} needs: ${finalNeeds.join(', ')}`);
    return finalNeeds;
  };

  // Weighted random selection favoring top players
  const weightedRandomPick = (players: Player[]): Player => {
    if (players.length === 1) return players[0];
    
    const rand = Math.random();
    
    // 50% chance for top player, 30% for second, 20% for third
    if (rand < 0.5) return players[0];
    if (rand < 0.8 && players.length > 1) return players[1];
    return players[Math.min(2, players.length - 1)];
  };

  // Make an AI pick for a team - ALMOST INSTANT
  const makeAIPick = (teamId: number) => {
    if (draftStatusRef.current !== 'active' || draftState.availablePlayers.length === 0) {
      console.log('⚠️ Cannot make AI pick - draft not active or no available players', {
        draftActive: draftStatusRef.current === 'active',
        availablePlayers: draftState.availablePlayers.length
      });
      return;
    }
    
    // Double-check it's still this team's turn
    if (getCurrentPickingTeam() !== teamId) {
      console.log('⚠️ AI pick canceled - not this team\'s turn anymore', {
        expectedTeam: teamId,
        actualTeam: getCurrentPickingTeam()
      });
      return;
    }
    
    const currentRound = draftState.round;
    const teamNeeds = getTeamNeeds(teamId, currentRound);
    
    console.log(`Team ${teamId} needs: ${teamNeeds.join(', ')} (Round ${currentRound})`);
    
    // Filter players by position needs
    let candidatePlayers = draftState.availablePlayers.filter(player => 
      teamNeeds.includes(player.position)
    );
    
    // If no players match needs, take best available
    if (candidatePlayers.length === 0) {
      candidatePlayers = draftState.availablePlayers.filter(player => 
        ['RB', 'WR', 'TE', 'QB'].includes(player.position)
      );
    }
    
    // Sort by projection and take top 3
    const topCandidates = candidatePlayers
      .sort((a, b) => (b.projection || 0) - (a.projection || 0))
      .slice(0, 3);
    
    if (topCandidates.length === 0) {
      console.log('No suitable candidates for AI pick');
      return;
    }
    
    // Make weighted random selection from top 3
    const aiPlayer = weightedRandomPick(topCandidates);
    
    console.log(`🤖 Team ${teamId} picks: ${aiPlayer.name} (${aiPlayer.position}) - ${aiPlayer.projection || 0} pts`);
    
    // Add to recent picks for display
    setRecentPicks(prev => [
      {
        pick: draftState.currentPick,
        teamId,
        playerName: aiPlayer.name,
        position: aiPlayer.position,
        team: aiPlayer.team || 'UNK',
        timestamp: new Date()
      },
      ...prev
    ].slice(0, 10)); // Keep only last 10 picks
    
    makePick(String(aiPlayer.id), teamId);
    
    // Next pick will be automatically processed by useEffect when state updates
  };

  // Handle user making a pick
  const handleUserPick = (playerId: string) => {
    if (draftStatus !== 'active' || !isUserTurn) {
      console.log('Cannot make user pick - draft not active or not user turn', {
        draftStatus,
        isUserTurn,
        getCurrentPickingTeam: getCurrentPickingTeam(),
        userTeamId: draftState.userTeamId
      });
      return;
    }
    
    const player = draftState.availablePlayers.find(p => String(p.id) === playerId);
    if (!player) {
      console.log('Player not found', { playerId, availableCount: draftState.availablePlayers.length });
      return;
    }
    
    // Check position cap
    if (!canDraftPosition(player.position)) {
      const positionCaps = getPositionCaps();
      const cap = positionCaps[player.position as keyof typeof positionCaps] || 6;
      console.log(`Cannot draft ${player.name} - ${player.position} roster is full (${cap} max)`);
      alert(`Cannot draft ${player.name} - you already have the maximum number of ${player.position}s allowed (${cap})`);
      return;
    }
    
    console.log(`🙋‍♂️ User picks player: ${player.name} (${player.position}) at pick ${draftState.currentPick}`);
    
    // Clear any pending AI timeouts to prevent conflicts
    if (currentPickTimeout) {
      clearTimeout(currentPickTimeout);
      setCurrentPickTimeout(null);
      console.log('⏰ Cleared pending AI pick timeout');
    }
    
    // Add user pick to recent picks
    setRecentPicks(prev => [
      {
        pick: draftState.currentPick,
        teamId: draftState.userTeamId,
        playerName: player.name,
        position: player.position,
        team: player.team || 'UNK',
        timestamp: new Date()
      },
      ...prev
    ].slice(0, 10)); // Keep only last 10 picks
    
    // Close player search interface immediately
    setShowPlayerSearch(false);
    
    // Make the pick - this will trigger the useEffect to process the next pick
    makePick(playerId, draftState.userTeamId);
    
    console.log('✅ User pick completed, next pick will be processed automatically');
  };

  if (loading) {
    return (
      <PageLoader 
        pageName="Draft Assistant"
        subtitle="Get personalized draft recommendations based on your league settings and draft strategy"
        loadingText="Loading draft data..."
      />
    );
  }

  // Handle error states that could cause black screen
  if (error && !draftState.availablePlayers.length) {
    return (
      <div className="pt-20 bg-black min-h-screen">
        <div className="max-w-7xl mx-auto py-4 md:py-8 px-3 md:px-4 text-white">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4 tracking-tight drop-shadow text-fiesta-yellow">
              Draft Assistant
            </h1>
            <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 max-w-md mx-auto">
              <h2 className="text-xl font-bold text-red-200 mb-3">⚠️ Error Loading Draft Data</h2>
              <p className="text-red-200 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="pt-20 bg-black min-h-screen">
        <div className="max-w-7xl mx-auto py-4 md:py-8 px-3 md:px-4 text-white">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4 tracking-tight drop-shadow text-fiesta-yellow">
            Draft Assistant
          </h1>
          <p className="text-base md:text-xl text-fiesta-blue/90 max-w-2xl mx-auto">
            Get personalized draft recommendations based on your league settings and draft strategy.
          </p>
        </div>

      {/* League Settings Panel */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl md:text-2xl font-extrabold text-fiesta-yellow flex items-center">
              🏈 League Settings
            </h2>
            {hasImportedSettings && (
              <div className="bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-lg px-3 py-1">
                <span className="text-purple-300 text-xs">✨ From Sleeper: </span>
                <span className="font-medium text-purple-100 text-xs">{leagueData.leagueName}</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="px-3 md:px-4 py-2 bg-fiesta-blue hover:bg-fiesta-blue/80 text-black font-semibold rounded-lg transition-all duration-200 text-sm md:text-base transform hover:scale-105"
          >
            {showSettings ? 'Hide Settings' : 'Show Settings'}
          </button>
        </div>

        {showSettings && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {/* Basic Settings */}
            <div className="space-y-4 p-4 bg-gray-750 rounded-lg border border-gray-600">
              <h3 className="text-lg font-medium text-secondary flex items-center">
                <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                Basic Settings
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  League Size
                </label>
                <select
                  value={leagueSettings.teams}
                  onChange={(e) => handleLeagueSettingsChange('teams', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {[8, 10, 12, 14, 16].map(size => (
                    <option key={size} value={size}>{size} Teams</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Scoring Format
                </label>
                <select
                  value={leagueSettings.scoringFormat}
                  onChange={(e) => handleLeagueSettingsChange('scoringFormat', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="standard">Standard</option>
                  <option value="ppr">PPR</option>
                  <option value="half-ppr">Half PPR</option>
                </select>
              </div>
            </div>

            {/* Draft Settings */}
            <div className="space-y-4 p-4 bg-gray-750 rounded-lg border border-gray-600">
              <h3 className="text-lg font-medium text-secondary flex items-center">
                <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                Draft Settings
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Draft Type
                </label>
                <select
                  value={draftType}
                  onChange={(e) => setDraftType(e.target.value as 'snake' | 'traditional')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={draftStatus !== 'inactive'}
                >
                  <option value="snake">Snake Draft</option>
                  <option value="traditional">Traditional Draft</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Draft Position
                </label>
                <select
                  value={draftPositionMode}
                  onChange={(e) => setDraftPositionMode(e.target.value as 'manual' | 'random')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary mb-2"
                  disabled={draftStatus !== 'inactive'}
                >
                  <option value="manual">Choose Position</option>
                  <option value="random">Random Position</option>
                </select>
                
                {draftPositionMode === 'manual' && (
                  <select
                    value={draftPosition}
                    onChange={(e) => setDraftPosition(parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={draftStatus !== 'inactive'}
                  >
                    {Array.from({ length: leagueSettings.teams }, (_, i) => i + 1).map(pos => (
                      <option key={pos} value={pos}>Pick {pos}</option>
                    ))}
                  </select>
                )}
                
                {draftPositionMode === 'random' && (
                  <div className="text-sm text-gray-400 p-2 bg-gray-700 rounded border">
                    Position will be randomly assigned when draft starts
                  </div>
                )}
              </div>
            </div>

            {/* Roster Positions */}
            <div className="space-y-4 p-4 bg-gray-750 rounded-lg border border-gray-600">
              <h3 className="text-lg font-medium text-secondary flex items-center">
                <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                Starting Lineup
              </h3>
              
              {Object.entries(leagueSettings.rosterPositions).map(([position, count]) => (
                <div key={position}>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {position.toUpperCase()} {position === 'def' ? '(Defense)' : ''}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="3"
                    value={count}
                    onChange={(e) => handleRosterPositionChange(position, parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              ))}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bench Size
                </label>
                <input
                  type="number"
                  min="4"
                  max="10"
                  value={leagueSettings.benchSize}
                  onChange={(e) => handleLeagueSettingsChange('benchSize', parseInt(e.target.value) || 6)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Flex Positions */}
            <div className="space-y-4 p-4 bg-gray-750 rounded-lg border border-gray-600">
              <h3 className="text-lg font-medium text-secondary flex items-center">
                <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                Flex Positions
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  RB/WR/TE Flex
                </label>
                <input
                  type="number"
                  min="0"
                  max="3"
                  value={leagueSettings.flexPositions.rbWrTe}
                  onChange={(e) => handleFlexPositionChange('rbWrTe', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  RB/WR Flex
                </label>
                <input
                  type="number"
                  min="0"
                  max="2"
                  value={leagueSettings.flexPositions.rbWr}
                  onChange={(e) => handleFlexPositionChange('rbWr', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  WR/TE Flex
                </label>
                <input
                  type="number"
                  min="0"
                  max="2"
                  value={leagueSettings.flexPositions.wrTe}
                  onChange={(e) => handleFlexPositionChange('wrTe', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Superflex (QB/RB/WR/TE)
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  value={leagueSettings.flexPositions.qbRbWrTe}
                  onChange={(e) => handleFlexPositionChange('qbRbWrTe', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Draft Strategy Info */}
      {draftStrategy && (
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-lg">
          <h2 className="text-xl md:text-2xl font-extrabold text-fiesta-pink mb-4 flex items-center">
            📋 Recommended Strategy
          </h2>
          <div className="mb-4 p-3 bg-fiesta-blue/10 border border-fiesta-blue/30 rounded-lg">
            <p className="text-xs md:text-sm text-fiesta-blue">
              ✅ <strong>Personalized for your league:</strong> {leagueSettings.teams} teams, {leagueSettings.scoringFormat.toUpperCase()}, 
              Draft Position {draftPosition}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
            <div className="space-y-4">
              <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
                <h3 className="text-xl font-bold text-secondary mb-2 flex items-center">
                  <span className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center mr-3">
                    <span className="text-black font-bold text-sm">📈</span>
                  </span>
                  {draftStrategy.name}
                </h3>
                <p className="text-gray-300 mb-4 leading-relaxed">{draftStrategy.description}</p>
              </div>

              <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
                <h4 className="text-base md:text-lg font-extrabold text-fiesta-yellow mb-4 flex items-center">
                  🎯 Position Priority Guide
                </h4>
                <div className="mb-3 text-xs md:text-sm text-gray-400">
                  ⭐ = Higher priority to draft early | ⚫ = Lower priority
                </div>
                <div className="space-y-2 md:space-y-3">
                  {Object.entries(draftStrategy.positionPriorities).map(([pos, priority]) => (
                    <div key={pos} className="flex items-center justify-between p-2 md:p-3 bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
                        <span className="text-gray-200 font-bold text-base md:text-lg min-w-[30px] md:min-w-[40px]">{pos}</span>
                        <span className="text-xs md:text-sm text-gray-400 truncate">
                          {pos === 'QB' ? '🏈 Quarterback' :
                           pos === 'RB' ? '🏃 Running Back' :
                           pos === 'WR' ? '🙌 Wide Receiver' :
                           pos === 'TE' ? '💪 Tight End' :
                           pos === 'K' ? '🦵 Kicker' :
                           pos === 'DEF' ? '🛡️ Defense' : pos}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: 5 }, (_, i) => (
                            <div
                              key={i}
                              className={`w-3 h-3 md:w-4 md:h-4 rounded-full transition-colors ${
                                i < Number(priority) ? 'bg-yellow-400 shadow-sm' : 'bg-gray-600'
                              }`}
                              title={i < Number(priority) ? 'High Priority' : 'Lower Priority'}
                            />
                          ))}
                        </div>
                        <span className="ml-1 md:ml-2 text-xs md:text-sm font-medium text-gray-300">
                          {Number(priority) >= 4 ? 'High' : Number(priority) >= 3 ? 'Medium' : 'Low'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="bg-gray-750 rounded-lg p-4 border border-gray-600 flex flex-col justify-center">
              <h4 className="text-lg font-medium text-secondary mb-4 flex items-center">
                <span className="w-6 h-6 bg-secondary rounded flex items-center justify-center mr-2">
                  <span className="text-black font-bold text-xs">⚖️</span>
                </span>
                Draft Focus by Round
              </h4>
              <div className="space-y-4">
                <div className="p-3 bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-lg">🔥</span>
                    <h5 className="font-medium text-white">Early Rounds (1-3)</h5>
                  </div>
                  <p className="text-sm text-gray-300">{draftStrategy.earlyRoundFocus.join(', ')}</p>
                </div>
                <div className="p-3 bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-lg">📈</span>
                    <h5 className="font-medium text-white">Mid Rounds (4-8)</h5>
                  </div>
                  <p className="text-sm text-gray-300">{draftStrategy.midRoundFocus.join(', ')}</p>
                </div>
                <div className="p-3 bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-lg">🎯</span>
                    <h5 className="font-medium text-white">Late Rounds (9+)</h5>
                  </div>
                  <p className="text-sm text-gray-300">{draftStrategy.lateRoundFocus.join(', ')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Draft Actions */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-lg">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4 items-start sm:items-center">
          {!isUserTurn ? (
            <>
              <button 
                onClick={handleStartDraft}
                disabled={isRunningMockDraft || loading}
                className={`px-4 md:px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-extrabold rounded-lg transition-all duration-200 text-sm md:text-base transform hover:scale-105 ${
                  isRunningMockDraft || loading ? 'opacity-50 cursor-not-allowed transform-none' : ''
                }`}
              >
                {isRunningMockDraft ? '🎯 Starting...' : '🚀 Start Mock Draft'}
              </button>
              <div className="text-xs md:text-sm text-gray-400">
                📝 Configure your league settings and draft position, then start your mock draft
              </div>
            </>
          ) : (
            <>
              <button 
                onClick={handleEndDraft}
                className="px-4 md:px-6 py-3 bg-fiesta-pink hover:bg-fiesta-pink/80 text-white font-extrabold rounded-lg transition-all duration-200 text-sm md:text-base transform hover:scale-105"
              >
                🏁 End Draft
              </button>
              <button 
                onClick={() => setShowPlayerSearch(!showPlayerSearch)} 
                className="px-4 md:px-6 py-3 bg-fiesta-blue hover:bg-fiesta-blue/80 text-black font-extrabold rounded-lg transition-all duration-200 text-sm md:text-base transform hover:scale-105"
                disabled={!isUserTurn}
              >
                🔍 {showPlayerSearch ? 'Hide Player Search' : 'Search Players'}
              </button>
              <div className="text-xs md:text-sm text-gray-400 flex flex-col sm:flex-row sm:items-center">
                <span className="mb-1 sm:mb-0 sm:mr-4">
                  <strong>Pick {draftState.currentPick}</strong> (Round {draftState.round})
                </span>
                <span className="mb-1 sm:mb-0 sm:mr-4">
                  Your Turn: <span className={`font-medium ${isUserTurn ? 'text-green-400' : 'text-red-400'}`}>
                    {isUserTurn ? '✅ Yes' : '❌ No'}
                  </span>
                </span>
                <span className="mb-1 sm:mb-0">
                  Roster: <span className="font-medium text-white">{draftState.userRoster.length} players</span>
                </span>
                {draftState.userRoster.length > 0 && (
                  <div className="flex items-center space-x-3 text-xs">
                    {(() => {
                      const positionCounts = draftState.userRoster.reduce((acc, p) => {
                        acc[p.position] = (acc[p.position] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>);
                      const caps = getPositionCaps();
                      
                      return Object.entries(positionCounts).map(([position, count]) => {
                        const cap = caps[position as keyof typeof caps] || 6;
                        const isAtCap = count >= cap;
                        return (
                          <span 
                            key={position} 
                            className={`px-2 py-1 rounded ${
                              isAtCap ? 'bg-red-900 text-red-200' : 'bg-gray-700 text-gray-300'
                            }`}
                          >
                            {position}: {count}/{cap}
                          </span>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Player Search Section */}
      {showPlayerSearch && isUserTurn && (
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-lg">
          <h2 className="text-xl md:text-2xl font-extrabold text-fiesta-blue mb-4 flex items-center">
            🔍 Search & Draft Players
          </h2>
          {!searchQuery && (
            <p className="text-xs md:text-sm text-gray-400 mb-4">
              🎯 <strong>Suggestions:</strong> Showing highest projected players not in your top 8 recommendations
            </p>
          )}
          
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by player name, position, or team..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 md:px-4 py-2 md:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm md:text-base"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 max-h-80 md:max-h-96 overflow-y-auto">
            {filteredPlayers.map((player) => (
              <div key={player.id} className="bg-gray-700 rounded-lg p-3 md:p-4 hover:bg-gray-600 transition-colors border border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                    <img
                      src={getPlayerImage(player.name)}
                      alt={player.name || 'Player'}
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-gray-600 flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-white truncate text-sm md:text-base">{player.name || 'Unknown Player'}</h4>
                      <div className="flex items-center space-x-1 md:space-x-2 mt-1">
                        <span className={`px-1 md:px-2 py-1 rounded text-xs font-medium ${
                          (player.position || '') === 'QB' ? 'bg-red-900 text-red-200' :
                          (player.position || '') === 'RB' ? 'bg-green-900 text-green-200' :
                          (player.position || '') === 'WR' ? 'bg-blue-900 text-blue-200' :
                          (player.position || '') === 'TE' ? 'bg-yellow-900 text-yellow-200' :
                          'bg-gray-900 text-gray-200'
                        }`}>
                          {player.position || 'N/A'}
                        </span>
                        <span className="text-xs md:text-sm text-gray-300">{player.team || 'N/A'}</span>
                      </div>
                      <div className="text-xs md:text-sm text-gray-400 mt-1">
                        Proj: {player.projection || 0} pts
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const playerPosition = player.position || 'UNKNOWN';
                    const canDraft = canDraftPosition(playerPosition);
                    
                    if (!canDraft) {
                      const positionCaps = getPositionCaps();
                      const cap = positionCaps[playerPosition as keyof typeof positionCaps] || 6;
                      return (
                        <button
                          disabled
                          className="px-3 py-2 bg-gray-600 cursor-not-allowed text-white rounded font-medium text-xs"
                        >
                          Full ({cap})
                        </button>
                      );
                    }
                    
                    return (
                      <button
                        onClick={() => handleUserPick(String(player.id))}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium transition-colors text-sm"
                      >
                        Draft
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
          
          {filteredPlayers.length === 0 && searchQuery && (
            <div className="text-center py-8">
              <p className="text-gray-400">No players found matching "{searchQuery}"</p>
            </div>
          )}
        </div>
      )}

      {/* Draft Status */}
      {draftStatus === 'inactive' && (
        <div className="bg-gradient-to-r from-gray-700 to-gray-750 rounded-xl p-6 mb-6 border border-gray-600 shadow-lg">
          <h2 className="text-xl font-extrabold text-fiesta-pink mb-3 flex items-center">
            ⏳ Draft Not Started
          </h2>
          <p className="text-fiesta-pink/80 mb-3">
            Configure your league settings above and click "Start Mock Draft" to begin your draft simulation.
          </p>
          <div className="text-sm text-fiesta-yellow">
            💡 Your recommendations will adapt based on your draft position and league settings.
          </div>
        </div>
      )}

      {draftStatus === 'active' && !isUserTurn && (
        <div className="bg-gradient-to-r from-fiesta-yellow/10 to-fiesta-pink/5 rounded-xl p-6 mb-6 border border-fiesta-yellow/30 shadow-lg">
          <h2 className="text-xl font-extrabold text-fiesta-yellow mb-3 flex items-center">
            ⏰ Waiting for Team {getCurrentPickingTeam()} to Pick
          </h2>
          <div className="space-y-2">
            <p className="text-fiesta-yellow/80">
              Pick {draftState.currentPick} (Round {draftState.round}) - Team {getCurrentPickingTeam()} is currently drafting...
            </p>
            {currentPickingStatus && (
              <p className="text-fiesta-blue font-semibold">
                🤖 {currentPickingStatus}
              </p>
            )}
          </div>
        </div>
      )}

      {isUserTurn && (
        <div className="bg-gradient-to-r from-fiesta-blue/10 to-fiesta-yellow/10 rounded-xl p-6 mb-6 border border-fiesta-blue/30 shadow-lg">
          <h2 className="text-xl font-extrabold text-fiesta-blue mb-3 flex items-center">
            🎯 Your Turn to Pick!
          </h2>
          <p className="text-fiesta-blue/80">
            Pick {draftState.currentPick} (Round {draftState.round}) - Choose from the recommendations below or search for any player.
          </p>
        </div>
      )}

      {/* Draft Completion Screen */}
      {isDraftCompleted && (
        <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 rounded-xl p-8 mb-6 border border-green-500/30 shadow-lg">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-3xl font-bold text-green-300 mb-2">Draft Complete!</h2>
            <p className="text-green-200 text-lg">
              Congratulations! You've successfully completed your fantasy football draft.
            </p>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <span className="mr-2">👤</span>
              Your Final Roster ({draftState.userRoster.length} players)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {draftState.userRoster.map((player: Player, idx: number) => (
                <div key={player.id} className="bg-gradient-to-r from-gray-700 to-gray-750 rounded-lg p-4 border border-gray-600 hover:border-primary/50 transition-all duration-200">
                  <div className="flex items-center space-x-3">
                    <img
                      src={getPlayerImage(player.name)}
                      alt={player.name || 'Player'}
                      className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-white truncate">{player.name}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          player.position === 'QB' ? 'bg-red-900 text-red-200' :
                          player.position === 'RB' ? 'bg-green-900 text-green-200' :
                          player.position === 'WR' ? 'bg-blue-900 text-blue-200' :
                          player.position === 'TE' ? 'bg-yellow-900 text-yellow-200' :
                          'bg-gray-900 text-gray-200'
                        }`}>
                          {player.position}
                        </span>
                        <span className="text-sm text-gray-300">{player.team}</span>
                      </div>
                      <div className="text-sm text-gray-400 mt-1">
                        Round {Math.floor(idx / leagueSettings.teams) + 1} • {player.projection || 0} pts
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-primary mb-1">
                {draftState.userRoster.reduce((sum, p) => sum + (p.projection || 0), 0).toFixed(1)}
              </div>
              <div className="text-sm text-gray-400">Total Projected Points</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-secondary mb-1">
                {draftState.userRoster.length}
              </div>
              <div className="text-sm text-gray-400">Players Drafted</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-400 mb-1">
                {Object.values(
                  draftState.userRoster.reduce((acc, p) => {
                    acc[p.position] = (acc[p.position] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>)
                ).length}
              </div>
              <div className="text-sm text-gray-400">Positions Filled</div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => {
                setIsDraftCompleted(false);
                handleEndDraft();
              }}
              className="px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              🔄 Start New Draft
            </button>
          </div>
        </div>
      )}

      {/* Recent Picks during active draft */}
      {draftStatus === 'active' && recentPicks.length > 0 && (
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-lg">
          <h2 className="text-xl md:text-2xl font-extrabold text-fiesta-pink mb-4 flex items-center">
            📊 Recent Picks
          </h2>
          <div className="space-y-2 md:space-y-3">
            {recentPicks.slice(0, 5).map((pick) => (
              <div 
                key={`${pick.pick}-${pick.playerName}`}
                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 rounded-lg transition-colors ${
                  pick.teamId === draftState.userTeamId 
                    ? 'bg-green-900/30 border border-green-500/30' 
                    : 'bg-gray-700 border border-gray-600'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-400">#{pick.pick}</span>
                    <span className={`text-sm font-medium ${
                      pick.teamId === draftState.userTeamId ? 'text-green-300' : 'text-gray-300'
                    }`}>
                      {pick.teamId === draftState.userTeamId ? 'YOU' : `Team ${pick.teamId}`}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 flex-wrap">
                    <span className="font-bold text-white text-sm md:text-base">{pick.playerName}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      pick.position === 'QB' ? 'bg-red-900 text-red-200' :
                      pick.position === 'RB' ? 'bg-green-900 text-green-200' :
                      pick.position === 'WR' ? 'bg-blue-900 text-blue-200' :
                      pick.position === 'TE' ? 'bg-yellow-900 text-yellow-200' :
                      'bg-gray-900 text-gray-200'
                    }`}>
                      {pick.position}
                    </span>
                    <span className="text-xs md:text-sm text-gray-400">{pick.team}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2 sm:mt-0">
                  {pick.timestamp.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
          </div>
          {recentPicks.length > 5 && (
            <div className="text-center mt-3">
              <span className="text-sm text-gray-400">
                Showing latest 5 of {recentPicks.length} picks
              </span>
            </div>
          )}
        </div>
      )}

      {/* Draft Recommendations */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-4 md:p-6 shadow-lg">
        <h2 className="text-xl md:text-2xl font-extrabold text-fiesta-yellow mb-4 flex items-center">
          🎯 Draft Recommendations
        </h2>
        
        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 mb-4">
            <p className="text-red-200">Error: {error}</p>
          </div>
        )}

        {recommendations.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No recommendations available. Please check your league settings and try again.</p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {recommendations.map((rec: DraftRecommendation, idx: number) => (
              <div key={rec.player.id} className="bg-gradient-to-r from-gray-700 to-gray-750 rounded-xl p-4 md:p-5 hover:from-gray-650 hover:to-gray-700 transition-all duration-300 border border-gray-600 hover:border-primary/30 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex items-start space-x-3 md:space-x-4 flex-1">
                    {/* Player Image */}
                    <div className="flex-shrink-0">
                      <div className="relative">
                        <img
                          src={getPlayerImage(rec.player.name)}
                          alt={rec.player.name || 'Player'}
                          className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-gray-600"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png';
                          }}
                        />
                        <div className="absolute -top-1 -right-1 w-5 h-5 md:w-6 md:h-6 bg-fiesta-blue rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-black">#{idx + 1}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Player Info */}
                    <div className="flex-1 min-w-0">
                      {/* Player Name - Left aligned next to image */}
                      <div className="mb-2">
                        <h3 className="text-lg md:text-xl font-bold text-white truncate text-left">
                          {rec.player.name || 'Unknown Player'}
                        </h3>
                      </div>
                      
                      {/* Position and Team Info */}
                      <div className="mb-3">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            (rec.player.position || '') === 'QB' ? 'bg-red-900 text-red-200' :
                            (rec.player.position || '') === 'RB' ? 'bg-green-900 text-green-200' :
                            (rec.player.position || '') === 'WR' ? 'bg-blue-900 text-blue-200' :
                            (rec.player.position || '') === 'TE' ? 'bg-yellow-900 text-yellow-200' :
                            'bg-gray-900 text-gray-200'
                          }`}>
                            {rec.player.position || 'N/A'}
                          </span>
                          <span className="text-gray-300">{rec.player.team || 'N/A'}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-300">Tier {rec.tier || 'N/A'}</span>
                        </div>
                      </div>
                      
                      {/* Reasoning */}
                      <p className="text-gray-300 text-xs md:text-sm mb-3 leading-relaxed">{rec.reasoning}</p>
                      
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                        <div className="text-center">
                          <div className="text-base md:text-lg font-bold text-fiesta-blue">{rec.value.toFixed(1)}</div>
                          <div className="text-xs text-gray-400">Value</div>
                        </div>
                        <div className="text-center">
                          <div className="text-base md:text-lg font-bold text-fiesta-pink">{rec.positionalNeed}</div>
                          <div className="text-xs text-gray-400">Need</div>
                        </div>
                        <div className="text-center">
                          <div className="text-base md:text-lg font-bold text-fiesta-yellow">{rec.player.projection || 0}</div>
                          <div className="text-xs text-gray-400">Projection</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-base md:text-lg font-bold ${
                            rec.riskLevel === 'low' ? 'text-fiesta-blue' : 
                            rec.riskLevel === 'medium' ? 'text-fiesta-yellow' : 'text-fiesta-pink'
                          }`}>
                            {rec.riskLevel.toUpperCase()}
                          </div>
                          <div className="text-xs text-gray-400">Risk</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  <div className="flex-shrink-0 sm:ml-4">
                    {(() => {
                      const recPlayerPosition = rec.player.position || 'UNKNOWN';
                      const canDraft = canDraftPosition(recPlayerPosition);
                      const isDisabled = !isUserTurn || !canDraft;
                      
                      let buttonText = 'Draft Player';
                      if (draftStatus === 'inactive') {
                        buttonText = 'Start Draft First';
                      } else if (!isUserTurn) {
                        buttonText = 'Not Your Turn';
                      } else if (!canDraft) {
                        const positionCaps = getPositionCaps();
                        const cap = positionCaps[recPlayerPosition as keyof typeof positionCaps] || 6;
                        buttonText = `${recPlayerPosition} Full (${cap} max)`;
                      }
                      
                                              return (
                          <button
                            onClick={() => handleUserPick(String(rec.player.id))}
                            disabled={isDisabled}
                            className={`w-full sm:w-auto px-4 md:px-6 py-3 ${
                              !isDisabled
                                ? 'bg-fiesta-yellow hover:bg-fiesta-yellow/80 text-black' 
                                : 'bg-gray-600 cursor-not-allowed text-white'
                            } rounded-lg font-extrabold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm`}
                          >
                            {buttonText}
                          </button>
                        );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mock Draft Results */}
      {mockDraftResult && showMockResults && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-850 rounded-xl p-6 mt-6 border border-gray-600 shadow-lg">
          <h2 className="text-2xl font-semibold text-primary mb-4 flex items-center">
            <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
            🎯 Mock Draft Results
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mockDraftResult.teams.map((team) => (
              <div key={team.teamId} className={`bg-gray-700 rounded-lg p-4 border ${
                team.teamId === draftPosition - 1 ? 'border-primary bg-primary/10' : 'border-gray-600'
              }`}>
                <h3 className="font-bold text-white mb-2">
                  {team.teamId === draftPosition - 1 ? '👤 Your Team' : `Team ${team.teamId + 1}`}
                </h3>
                <div className="text-sm text-gray-300 mb-2">
                  Projected Points: <span className="font-semibold text-secondary">{team.projectedPoints.toFixed(1)}</span>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {team.roster.slice(0, 6).map((player, playerIdx) => (
                    <div key={player.id} className="text-xs text-gray-400">
                      {playerIdx + 1}. {player.name || 'Unknown'} ({player.position || 'N/A'})
                    </div>
                  ))}
                  {team.roster.length > 6 && (
                    <div className="text-xs text-gray-500">
                      +{team.roster.length - 6} more players...
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <button
              onClick={() => setShowMockResults(false)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm"
            >
              Clear Results
            </button>
          </div>
        </div>
      )}

      {/* Current Roster */}
      {draftState.userRoster.length > 0 && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-850 rounded-xl p-6 mt-6 border border-gray-600 shadow-lg">
          <h2 className="text-2xl font-semibold text-primary mb-4 flex items-center">
            <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
            Your Roster ({draftState.userRoster.length} players)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {draftState.userRoster.map((player: Player, idx: number) => (
              <div key={player.id} className="bg-gradient-to-r from-gray-700 to-gray-750 rounded-lg p-4 border border-gray-600 hover:border-primary/30 transition-all duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-white truncate">{player.name || 'Unknown Player'}</h4>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        (player.position || '') === 'QB' ? 'bg-red-900 text-red-200' :
                        (player.position || '') === 'RB' ? 'bg-green-900 text-green-200' :
                        (player.position || '') === 'WR' ? 'bg-blue-900 text-blue-200' :
                        (player.position || '') === 'TE' ? 'bg-yellow-900 text-yellow-200' :
                        'bg-gray-900 text-gray-200'
                      }`}>
                        {player.position || 'N/A'}
                      </span>
                      <span className="text-sm text-gray-300">{player.team || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Round</div>
                    <div className="text-lg font-bold text-secondary">{Math.floor(idx / leagueSettings.teams) + 1}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
  } catch (renderError) {
    console.error('🚨 DraftAssistant render error:', renderError);
    
    // Return a fallback UI in case of rendering errors
    return (
      <div className="pt-20 bg-black min-h-screen">
        <div className="max-w-7xl mx-auto py-4 md:py-8 px-3 md:px-4 text-white">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4 tracking-tight drop-shadow text-fiesta-yellow">
              Draft Assistant
            </h1>
            <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 max-w-md mx-auto">
              <h2 className="text-xl font-bold text-red-200 mb-3">⚠️ Something went wrong</h2>
              <p className="text-red-200 mb-4">An unexpected error occurred while rendering the Draft Assistant.</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default DraftAssistant;
